import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Upload,
  FileText,
  Shield,
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  Calendar,
  RefreshCw,
  Download,
  History,
  X,
  AlertTriangle,
  File,
  Image,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KYCPage = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [status, setStatus] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialog, setUploadDialog] = useState({ open: false, requirement: null });
  const [historyDialog, setHistoryDialog] = useState({ open: false, documentType: null, history: [] });
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Fetch KYC status
  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/kyc/status`, { headers });
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch KYC status:', error);
    }
  };

  // Fetch KYC documents
  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, docsRes] = await Promise.all([
        axios.get(`${API_URL}/api/kyc/status`, { headers }),
        axios.get(`${API_URL}/api/kyc/documents`, { headers })
      ]);
      setStatus(statusRes.data);
      setDocuments(docsRes.data);
    } catch (error) {
      console.error('Failed to fetch KYC data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Status badge colors and icons
  const getStatusConfig = (overallStatus) => {
    switch (overallStatus) {
      case 'compliant':
        return {
          label: 'FICA Compliant',
          color: 'bg-green-100 text-green-700 border-green-200',
          bgColor: 'bg-gradient-to-br from-green-500 to-green-600',
          icon: ShieldCheck,
          iconColor: 'text-green-600'
        };
      case 'pending':
        return {
          label: 'Pending Review',
          color: 'bg-amber-100 text-amber-700 border-amber-200',
          bgColor: 'bg-gradient-to-br from-amber-500 to-amber-600',
          icon: Clock,
          iconColor: 'text-amber-600'
        };
      case 'expired':
        return {
          label: 'Expired — Action Required',
          color: 'bg-red-100 text-red-700 border-red-200',
          bgColor: 'bg-gradient-to-br from-red-500 to-red-600',
          icon: ShieldX,
          iconColor: 'text-red-600'
        };
      case 'incomplete':
        return {
          label: 'Incomplete — Action Required',
          color: 'bg-red-100 text-red-700 border-red-200',
          bgColor: 'bg-gradient-to-br from-red-500 to-red-600',
          icon: ShieldAlert,
          iconColor: 'text-red-600'
        };
      default:
        return {
          label: 'Unknown',
          color: 'bg-gray-100 text-gray-700 border-gray-200',
          bgColor: 'bg-gray-500',
          icon: Shield,
          iconColor: 'text-gray-600'
        };
    }
  };

  const getDocStatusConfig = (effectiveStatus) => {
    switch (effectiveStatus) {
      case 'received':
        return { label: 'Received', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2 };
      case 'pending':
        return { label: 'Pending Review', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock };
      case 'rejected':
        return { label: 'Rejected', color: 'bg-red-100 text-red-700 border-red-200', icon: X };
      case 'expired':
        return { label: 'Expired', color: 'bg-red-100 text-red-700 border-red-200', icon: AlertCircle };
      case 'outstanding':
      default:
        return { label: 'Outstanding', color: 'bg-gray-100 text-gray-600 border-gray-200', icon: AlertTriangle };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // File handling
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      validateAndSetFile(e.target.files[0]);
    }
  };

  const validateAndSetFile = (file) => {
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload PDF, JPG, or PNG files only.');
      return;
    }

    if (file.size > maxSize) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !uploadDialog.requirement) return;

    setUploading(true);
    try {
      const fileType = selectedFile.type.includes('pdf') ? 'pdf' : 
                       selectedFile.type.includes('jpeg') ? 'jpg' : 'png';
      
      // Read file as base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          // Get base64 content (remove data:xxx;base64, prefix)
          const base64Content = e.target.result.split(',')[1];
          
          await axios.post(`${API_URL}/api/kyc/documents/upload`, {
            document_type: uploadDialog.requirement.id,
            file_name: selectedFile.name,
            file_type: fileType,
            file_size: selectedFile.size,
            expiry_date: uploadDialog.requirement.has_expiry ? 
              new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString() : null,
            file_content: base64Content
          }, { headers });

          toast.success('Document uploaded successfully! It will be reviewed by our team.');
          setUploadDialog({ open: false, requirement: null });
          setSelectedFile(null);
          fetchDocuments();
        } catch (error) {
          console.error('Upload error:', error);
          toast.error('Failed to upload document. Please try again.');
        } finally {
          setUploading(false);
        }
      };
      
      reader.onerror = () => {
        toast.error('Failed to read file. Please try again.');
        setUploading(false);
      };
      
      reader.readAsDataURL(selectedFile);
    } catch (error) {
      toast.error('Failed to upload document. Please try again.');
      setUploading(false);
    }
  };

  const fetchHistory = async (documentType) => {
    try {
      const response = await axios.get(`${API_URL}/api/kyc/documents/${documentType}/history`, { headers });
      setHistoryDialog({ open: true, documentType, history: response.data });
    } catch (error) {
      toast.error('Failed to fetch document history.');
    }
  };

  const handleDownload = async (docId) => {
    try {
      const response = await axios.get(`${API_URL}/api/kyc/documents/${docId}/download`, {
        headers,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'kyc_document.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download document.');
    }
  };

  const statusConfig = status ? getStatusConfig(status.overall_status) : null;
  const StatusIcon = statusConfig?.icon || Shield;

  // Calculate progress
  const progressPercent = status?.summary ? 
    Math.round((status.summary.approved / status.summary.total_required) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="kyc-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#212529] font-['Manrope']">KYC & FICA Compliance</h1>
          <p className="text-[#6C757D] mt-1">Manage your compliance documents and verification status</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchDocuments}
          className="border-gray-200"
          data-testid="refresh-btn"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Compliance Status Card */}
      {loading ? (
        <Skeleton className="h-48 w-full" />
      ) : status && (
        <Card className={`border-0 shadow-lg overflow-hidden`} data-testid="compliance-status-card">
          <div className={`${statusConfig.bgColor} text-white p-6`}>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                  <StatusIcon className="h-8 w-8" />
                </div>
                <div>
                  <Badge 
                    className="bg-white/20 text-white border-white/30 text-lg px-4 py-1 font-semibold"
                    data-testid="fica-status-badge"
                  >
                    {statusConfig.label}
                  </Badge>
                  <p className="text-white/80 mt-2 text-sm">
                    Entity Type: {status.entity_type?.charAt(0).toUpperCase() + status.entity_type?.slice(1)}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                <div className="text-center md:text-right">
                  <p className="text-white/70 text-sm">FICA Expiry</p>
                  <p className="text-xl font-bold" data-testid="fica-expiry-date">
                    {formatDate(status.fica_expiry_date)}
                  </p>
                </div>
                <div className="text-center md:text-right">
                  <p className="text-white/70 text-sm">
                    {status.days_overdue ? 'Days Overdue' : 'Days Until Expiry'}
                  </p>
                  <p className="text-xl font-bold" data-testid="days-indicator">
                    {status.days_overdue ? (
                      <span className="text-red-200">{status.days_overdue} days</span>
                    ) : status.days_until_expiry !== null ? (
                      status.days_until_expiry
                    ) : '-'}
                  </p>
                </div>
                <div className="text-center md:text-right col-span-2 md:col-span-1">
                  <p className="text-white/70 text-sm">Last Verified</p>
                  <p className="text-xl font-bold" data-testid="last-verified">
                    {formatDate(status.last_verified)}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Progress Bar */}
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#6C757D]">Compliance Progress</span>
              <span className="text-sm font-medium text-[#212529]">
                {status.summary?.approved || 0} of {status.summary?.total_required || 0} documents approved
              </span>
            </div>
            <Progress value={progressPercent} className="h-2" />
            <div className="flex justify-between mt-2 text-xs text-[#6C757D]">
              <span>{status.summary?.pending || 0} pending review</span>
              <span>{status.summary?.missing || 0} outstanding</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Document Checklist */}
      <div>
        <h2 className="text-xl font-semibold text-[#212529] font-['Manrope'] mb-4">Required Documents</h2>
        
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} className="h-32 w-full" />
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {documents.map((item, index) => {
              const docStatus = getDocStatusConfig(item.effective_status);
              const DocStatusIcon = docStatus.icon;
              
              return (
                <Card 
                  key={item.requirement.id} 
                  className="border-0 shadow-sm hover:shadow-md transition-shadow"
                  data-testid={`kyc-doc-card-${item.requirement.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                      {/* Document Info */}
                      <div className="flex-1">
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${docStatus.color}`}>
                            <DocStatusIcon className="h-5 w-5" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold text-[#212529]">{item.requirement.name}</h3>
                              {item.requirement.required && (
                                <Badge variant="outline" className="text-xs border-red-200 text-red-600">
                                  Required
                                </Badge>
                              )}
                              <Badge className={`${docStatus.color} border text-xs`}>
                                {docStatus.label}
                              </Badge>
                            </div>
                            <p className="text-sm text-[#6C757D] mt-1">{item.requirement.description}</p>
                            
                            {/* Current document info */}
                            {item.document && (
                              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center gap-2 text-sm">
                                  <FileText className="h-4 w-4 text-[#6C757D]" />
                                  <span className="font-medium text-[#212529]">{item.document.file_name}</span>
                                  <span className="text-[#6C757D]">•</span>
                                  <span className="text-[#6C757D]">{formatFileSize(item.document.file_size)}</span>
                                </div>
                                <div className="flex flex-wrap gap-4 mt-2 text-xs text-[#6C757D]">
                                  <span>Uploaded: {formatDate(item.document.uploaded_at)}</span>
                                  {item.document.expiry_date && (
                                    <span>Expires: {formatDate(item.document.expiry_date)}</span>
                                  )}
                                  {item.document.status === 'pending' && (
                                    <span className="text-amber-600 font-medium">Awaiting verification</span>
                                  )}
                                  {item.document.status === 'rejected' && (
                                    <span className="text-red-600 font-medium">
                                      Rejected: {item.document.rejection_reason}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2 lg:flex-col lg:items-end">
                        <Button
                          className="bg-[#1D2D2A] hover:bg-[#243D37] text-white"
                          onClick={() => setUploadDialog({ open: true, requirement: item.requirement })}
                          data-testid={`upload-btn-${item.requirement.id}`}
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          {item.document ? 'Upload New' : 'Upload'}
                        </Button>
                        
                        <div className="flex gap-2">
                          {item.document && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownload(item.document.id)}
                              className="border-gray-200"
                              data-testid={`download-btn-${item.requirement.id}`}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => fetchHistory(item.requirement.id)}
                            className="border-gray-200"
                            data-testid={`history-btn-${item.requirement.id}`}
                          >
                            <History className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Upload Dialog */}
      <Dialog open={uploadDialog.open} onOpenChange={(open) => {
        if (!open) {
          setUploadDialog({ open: false, requirement: null });
          setSelectedFile(null);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Upload {uploadDialog.requirement?.name}</DialogTitle>
            <DialogDescription>
              {uploadDialog.requirement?.description}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Drag and Drop Zone */}
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive 
                  ? 'border-[#1D2D2A] bg-[#1D2D2A]/5' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileChange}
              />
              
              {selectedFile ? (
                <div className="space-y-2">
                  <div className="w-12 h-12 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle2 className="h-6 w-6 text-green-600" />
                  </div>
                  <p className="font-medium text-[#212529]">{selectedFile.name}</p>
                  <p className="text-sm text-[#6C757D]">{formatFileSize(selectedFile.size)}</p>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}
                    className="text-red-600 hover:text-red-700"
                  >
                    Remove file
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="w-12 h-12 mx-auto rounded-full bg-gray-100 flex items-center justify-center">
                    <Upload className="h-6 w-6 text-[#6C757D]" />
                  </div>
                  <p className="font-medium text-[#212529]">Drag and drop your file here</p>
                  <p className="text-sm text-[#6C757D]">or click to browse</p>
                </div>
              )}
            </div>
            
            {/* File requirements */}
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs font-medium text-[#6C757D] mb-2">Accepted formats:</p>
              <div className="flex gap-2">
                <Badge variant="outline" className="text-xs">PDF</Badge>
                <Badge variant="outline" className="text-xs">JPG</Badge>
                <Badge variant="outline" className="text-xs">PNG</Badge>
                <span className="text-xs text-[#6C757D] ml-auto">Max 10MB</span>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setUploadDialog({ open: false, requirement: null }); setSelectedFile(null); }}
            >
              Cancel
            </Button>
            <Button
              className="bg-[#1D2D2A] hover:bg-[#243D37] text-white"
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              data-testid="submit-upload-btn"
            >
              {uploading ? 'Uploading...' : 'Upload Document'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialog.open} onOpenChange={(open) => {
        if (!open) setHistoryDialog({ open: false, documentType: null, history: [] });
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Upload History</DialogTitle>
            <DialogDescription>
              Previous versions of this document
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {historyDialog.history.length === 0 ? (
              <p className="text-center text-[#6C757D] py-8">No upload history found</p>
            ) : (
              historyDialog.history.map((doc, index) => {
                const docStatus = getDocStatusConfig(
                  doc.status === 'approved' ? 'received' : doc.status
                );
                return (
                  <div 
                    key={doc.id} 
                    className={`p-3 rounded-lg border ${
                      doc.is_current ? 'border-[#1D2D2A] bg-[#1D2D2A]/5' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{doc.file_name}</span>
                          {doc.is_current && (
                            <Badge className="bg-[#1D2D2A] text-white text-xs">Current</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-xs text-[#6C757D]">
                          <span>v{doc.version}</span>
                          <span>•</span>
                          <span>{formatDate(doc.uploaded_at)}</span>
                          <span>•</span>
                          <Badge className={`${docStatus.color} border text-xs`}>
                            {docStatus.label}
                          </Badge>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownload(doc.id)}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KYCPage;
